/**
 * RecapCardItem.f.jsx (SBR-3529 Phase 2-3 / Challenger 2 recap card component)
 * Renders a single related search term with a 2×2 grid of product thumbnail images.
 * Handles placeholder images for missing/failed slots and click-to-search navigation.
 */
import React from 'react';
import PropTypes from 'prop-types';
import { wrapFunctionalComponent } from 'utils/framework';
import { Box, Text, Image } from 'components/ui';
import { PLACEHOLDER_IMAGE_SRC } from 'components/RelatedSearches/constants';
import { space, colors } from 'style/config';
import localeUtils from 'utils/LanguageLocale';
import relatedSearchesEN from 'components/RelatedSearches/locales/relatedSearches_en_US';
import relatedSearchesFR from 'components/RelatedSearches/locales/relatedSearches_fr_CA';

const getText = localeUtils.getLocalisedDataFromResource(relatedSearchesEN, relatedSearchesFR);

/**
 * RecapCardItem: 2×2 grid card for recap variant.
 * Phase 2: Grid layout with placeholder handling
 * Phase 3: Term label + click handler with debounce (inherited from parent)
 * Phase 6: Accessibility (aria-label, keyboard focusable)
 */
function RecapCardItem({
    term, images, onClick, _isActive, placeholderSrc = PLACEHOLDER_IMAGE_SRC
}) {
    // Phase 2 (P2-001 through P2-004): Cap to 4 images, fill remaining slots with placeholders
    const gridImages = images.slice(0, 4); // P2-004: cap to first 4
    const slots = Array(4)
        .fill(null)
        .map((_, i) => gridImages[i] || { url: placeholderSrc, altText: '' }); // P2-002/P2-003: empty alt for decorative placeholders

    // Phase 3 (P3-002): Click handler navigates via parent's debounced handleTermClick
    const handleClick = () => {
        onClick(term);
    };

    // Phase 6 (P6-002) + Phase 7 (P7-003): Localized aria-label
    const ariaLabel = getText('searchForTerm', [term]);

    return (
        <Box
            is='li'
            data-testid='recap-card-item'
        >
            <button
                type='button'
                css={styles.button}
                onClick={handleClick}
                aria-label={ariaLabel} // P6-002: aria-label for accessibility
            >
                {/* Phase 3 (P3-001): Term label above grid */}
                <Text
                    data-testid='recap-card-term'
                    fontSize='sm'
                    fontWeight='bold'
                    marginBottom={1}
                    children={term}
                />

                {/* Phase 2 (P2-001): 2×2 grid layout */}
                <Box
                    data-testid='recap-card-grid'
                    css={styles.grid}
                >
                    {slots.map((img, i) => (
                        <Box
                            key={i}
                            data-testid={`image-slot-${i}`}
                            css={styles.imageSlot}
                        >
                            <Image
                                src={img.url}
                                alt={img.altText}
                                css={styles.image}
                            />
                        </Box>
                    ))}
                </Box>
            </button>
        </Box>
    );
}

// Co-located styles (UFE convention)
const styles = {
    button: {
        width: '100%',
        padding: 0,
        border: 'none',
        background: 'transparent',
        cursor: 'pointer',
        textAlign: 'left',
        '&:focus': {
            outline: `2px solid ${colors.black}`,
            outlineOffset: `${space[1]}px`
        }
    },
    grid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)', // P2-001: 2×2 grid
        gap: space[1] // spacing between grid items
    },
    imageSlot: {
        position: 'relative',
        paddingBottom: '100%', // P2-005: 1:1 aspect ratio
        overflow: 'hidden',
        backgroundColor: colors.gray[100] // light gray background for empty slots
    },
    image: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        objectFit: 'cover' // P2-005: maintain aspect ratio, cover slot
    }
};

RecapCardItem.propTypes = {
    term: PropTypes.string.isRequired,
    images: PropTypes.arrayOf(
        PropTypes.shape({
            url: PropTypes.string.isRequired,
            altText: PropTypes.string
        })
    ).isRequired,
    onClick: PropTypes.func.isRequired,
    isActive: PropTypes.bool,
    placeholderSrc: PropTypes.string
};

export default wrapFunctionalComponent(RecapCardItem, 'RecapCardItem');
